import React, { Fragment, useState } from "react";
import { Col, Row, Tooltip, Upload } from "antd";
import { AttachmentIcon } from "../../common/CustomIcon";
import FileCardCarousel from "./FileCardCarousel";
import BulkAttachmentUpload from "./BulkAttachmentUpload";
import { isOtherAttachmentBucketDoc } from "./documentsFileComponents";

// Upload / POD settings may use OTHER ATTACHMENT, OTHERS, or ATTACHMENT.
const OTHER_ATTACHMENT_ALIASES = new Set([
  "OTHER ATTACHMENT",
  "OTHER ATTACHMENTS",
  "OTHERS",
  "ATTACHMENT",
]);
const normalizePicCode = (code) => (code || "").trim().toUpperCase();
const isOtherAttachmentAlias = (code) =>
  OTHER_ATTACHMENT_ALIASES.has(normalizePicCode(code));
const isPredefinedDocCode = (predefinedDocsCode, docPicCode) =>
  (predefinedDocsCode || []).some(
    (code) => normalizePicCode(code) === normalizePicCode(docPicCode)
  );
const docMatchesPicBucket = (docPicCode, bucketCode) => {
  if (isOtherAttachmentAlias(bucketCode)) {
    return isOtherAttachmentAlias(docPicCode);
  }
  return normalizePicCode(docPicCode) === normalizePicCode(bucketCode);
};

const UploadButton_v2 = (props) => {
  const {
    documents = [],
    stagedDocs = [],
    selectedDocs = [],
    label = "PODs",
    docCode,
    isOtherAttachment = false,
    handleChange = () => {},
    toggleSelectDocument = () => {},
    predefinedDocsCode = [],
    onPrint = () => {},
    requestedAs = "",
    setIsPODemailModalVisible,
    currentOrder,
    setPreviewVisible,
    previewVisible,
    setPreviewFileUrl,
    setPreviewFileType,
    refreshDetails = () => {},
    show,
    setShow,
    locObj,
    isShipper = false,
    previewDocs,
    locked,
    isAllowedToUploadDocsForLockedInvoices = false,
    handleDelete = () => {},
    handleUpload = () => {},
    setStagedDocs = () => {},
    isUploading = false,
    disableBulkUpload = false,
    picSettings = [],
    actionType = "view",
    handleCreatePics = () => {},
    allowInternalToggle = false,
    hideDetailsColumn = false,
  } = props;

  const [isBulkUploadVisible, setIsBulkUploadVisible] = useState(false);
  const docs = documents.filter((record) => record.image_type !== "print");

  // Only the docs matching this button's own doc type (e.g. POD, BOL) should
  // show up in its modal - not every document across all types. When this is
  // the "other attachments" bucket, all non-mapped docs are clubbed together
  // (plus any alias codes like OTHERS / ATTACHMENT).
  const docsForThisType = isOtherAttachment
    ? docs.filter(
        (doc) =>
          isOtherAttachmentAlias(doc.pic_code) ||
          !isPredefinedDocCode(predefinedDocsCode, doc.pic_code)
      )
    : docs.filter((doc) => docMatchesPicBucket(doc.pic_code, docCode));

  const stagedDocsForThisType = stagedDocs.filter(
    isOtherAttachment
      ? (doc) =>
          isOtherAttachmentAlias(doc.pic_code) ||
          !isPredefinedDocCode(predefinedDocsCode, doc.pic_code)
      : (doc) => docMatchesPicBucket(doc.pic_code, docCode)
  );
  const matchedDocument = isOtherAttachment
    ? docsForThisType[0]
    : docs.find((doc) => docMatchesPicBucket(doc.pic_code, docCode));

  // Every file that belongs to this button's doc type - staged (not yet saved)
  // docs first, then the already-uploaded ones - shown together in the carousel.
  const filesForThisType = [
    ...stagedDocsForThisType,
    ...docsForThisType,
  ];

  const uniqueFiles = [];
  const seenKeys = new Set();
  filesForThisType.forEach((file) => {
    const key = file?.ack_id || file?.id || file?.src;
    if (key && seenKeys.has(key)) return;
    if (key) seenKeys.add(key);
    uniqueFiles.push(file);
  });

  const renderUploadButton = () => {
    if (docCode === "CASH RECEIPT") {
      return (
        <div
          onClick={
            disableBulkUpload ? undefined : () => setIsBulkUploadVisible(true)
          }
          style={{
            width: "56px",
            height: "72px",
            border: "1px dashed #c7d2e0",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff"
          }}
        >
          {!isShipper ? <AttachmentIcon color="#576EC1" width="24px" height="24px" /> : <Fragment />}
        </div>
      );
    }
    return (
      <Upload.Dragger
        showUploadList={false}
        beforeUpload={(file) => {
          handleChange({ file }, { pic_title: docCode, pic_code: docCode });
          return false; // Prevent automatic upload
        }}
        disabled={isShipper || (locked && !isAllowedToUploadDocsForLockedInvoices)}
      >
        {!isShipper ? <AttachmentIcon color="#576EC1" width="24px" height="24px" /> : <Fragment />}
      </Upload.Dragger>
    );
  };

  const displayContent = () => {
    if (uniqueFiles.length > 0 || previewVisible)
      return (
        <FileCardCarousel
          files={
            uniqueFiles.length > 0
              ? uniqueFiles
              : [matchedDocument].filter(Boolean)
          }
          documents={docs}
          selectedDocs={selectedDocs}
          toggleSelectDocument={toggleSelectDocument}
          predefinedDocsCode={predefinedDocsCode}
          onPrint={onPrint}
          requestedAs={requestedAs}
          setIsPODemailModalVisible={setIsPODemailModalVisible}
          currentOrder={currentOrder}
          setPreviewVisible={setPreviewVisible}
          previewVisible={previewVisible}
          setPreviewFileUrl={setPreviewFileUrl}
          setPreviewFileType={setPreviewFileType}
          refreshDetails={refreshDetails}
          show={show}
          setShow={setShow}
          locObj={locObj}
          isShipper={isShipper}
          previewDocs={previewDocs}
        />
      );
    return renderUploadButton();
  };

  return (
    <Row
      type="flex"
      align="middle"
      justify="center"
      style={{ width: "56px", height: "96px", cursor: "pointer", gap: "8px" }}
    >
      <Col
        span={24}
        style={{
          width: "56px",
          height: "72px",
          textAlign: "center",
        }}
      >
        {displayContent()}
      </Col>
      <Col
        span={24}
        style={{
          width: "56px",
          height: "16px",
          textAlign: "center",
          fontSize: "10px",
          fontWeight: "700",
          fontFamily: "Lato",
          color: "#576EC1",
          lineHeight: "16px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {/* onClick moved onto the span itself - antd v3's Tooltip doesn't
            reliably forward arbitrary handlers like onClick to its trigger element */}
        <Tooltip title={label}>
          <span
            onClick={
              disableBulkUpload ? undefined : () => setIsBulkUploadVisible(true)
            }
            onMouseEnter={(e) => {
              if (!disableBulkUpload) e.currentTarget.style.textDecoration = "underline";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = "none";
            }}
            style={{
              cursor: disableBulkUpload ? "default" : "pointer",
              color: disableBulkUpload ? "inherit" : "#1890ff",
            }}
          >
            {label}
          </span>
        </Tooltip>
      </Col>
      {!disableBulkUpload && (
        <BulkAttachmentUpload
        visible={isBulkUploadVisible}
        onCancel={() => setIsBulkUploadVisible(false)}
        documents={docsForThisType}
        isfromDoc={true}
        onDelete={handleDelete}
        onUpload={async () => {
          // In view mode docs go straight to the API (an order already exists).
          // In create/full_edit the staged files must be flushed to the parent
          // via handleCreatePics -> handleCreatePicsInfo, otherwise they never
          // reach loc.documents and are missing from the save payload.
          if (actionType === "view") {
            await handleUpload();
          } else {
            handleCreatePics();
          }
          setIsBulkUploadVisible(false);
        }}
        onDocsChange={setStagedDocs}
        defaultPicCode={isOtherAttachment ? "OTHER ATTACHMENT" : docCode}
        defaultPicTitle={isOtherAttachment ? "OTHER ATTACHMENT" : docCode}
        isUploading={isUploading}
        picSettings={picSettings}
        allowInternalToggle={allowInternalToggle}
        hideDetailsColumn={hideDetailsColumn}
      />
      )}
    </Row>
  );
};

export default UploadButton_v2;